

"use client"

import type React from "react"
import { renderPatternGrid } from "./CreatePatterns"
import { ChevronDown } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import useWindowSize from "./utils/useWindowSize"

type PatternCoordinate = [number, number]

interface PatternData {
  pattern: PatternCoordinate[][]
  price?: number
  [key: string]: any
}

interface PatternType {
  [key: string]: PatternData
}

interface PatternItemProps {
  type: string
  prices: number
  draws: PatternType
  isMobile?: boolean
  isPatternAmount?: boolean
  winCount: number
}

const PatternItem: React.FC<PatternItemProps> = ({ isMobile, winCount, type, prices, draws, isPatternAmount }) => {
  const patternTypes = Object.keys(draws)
  const { width, height } = useWindowSize()
  const getMaxGridDimensions = () => {
    let maxRows = 0
    let maxCols = 0
    patternTypes.forEach((type) => {
      draws[type].pattern.forEach((subPattern: PatternCoordinate[]) => {
        const maxPatternRow = Math.max(...subPattern.map(([row]) => row)) + 1
        const maxPatternCol = Math.max(...subPattern.map(([, col]) => col)) + 1
        maxRows = Math.max(maxRows, maxPatternRow)
        maxCols = Math.max(maxCols, maxPatternCol)
      })
    })
    return { rows: Math.max(maxRows), cols: Math.max(maxCols) }
  }

  const { rows, cols } = getMaxGridDimensions()
  const CELL_SIZE = cols !== 10 && !isMobile ? 7 : 5

  const firstPattern = draws[type].pattern[0]

  const formatPatternType = (type: string) => {
    const formatted = type.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/\b\w/g, (char) => char.toUpperCase())
    return formatted
  }
  const isIpad = height >= 1300

  return (
    <div className="w-full mb-1 px-1 md:mb-3">
      <div
        className={`grid ${width <= 1024 ? 'grid-cols-[45%_30%_30%]' : 'grid-cols-[60%_20%_20%]'} py-2 px-1   bg-[#3D1FA0] items-center justify-center rounded-md`}
        title={formatPatternType(type)}
      >
        <div className="flex gap-x-2 px-1 justify-center items-center">
          {width > 1024 && (
            <div className="flex items-center justify-center">
              {renderPatternGrid({
                pattern: firstPattern,
                rows,
                cols,
                CELL_SIZE,
                blueStarSize: { height: 0.4, width: 0.4 },
                img: "blue",
                marginBottom: 0,
                marginRight: 0,
                isPatternAmount
              })}
            </div>
          )}
          <div className="flex-grow   text-start">
            <span className={`text-white font-medium ml-2 lg:ml-0 md:ml-2 flex md:text-sm text-xs  `}>
              {!isMobile
                ? formatPatternType(type).length > 14
                  ? formatPatternType(type).slice(0, 14) + "..."
                  : formatPatternType(type)
                : formatPatternType(type)}
            </span>
          </div>
        </div>

        <div className="flex flex-grow  items-center justify-center  rounded-md mx-2">
          <img src="/assets/cup3.png" alt="cup" className={`${isIpad ? 'w-[22px] h-[22px]' : 'md:w-[20px] w-[14px] h-[14px] md:h-[20px]'} `} />
          <span className={`text-white font-bold ${isIpad ? 'text-[18px]' : 'text-[10px]'} `}>{winCount}</span>
        </div>
        <div className="flex flex-grow  gap-1  items-center justify-center  flex-shrink-0 mx-2">
          <img src="/assets/coin.png" alt="Coin" className={`${isIpad ? 'w-[26px] h-[26px]' : 'md:w-[20px] w-[14px] h-[14px] md:h-[20px]'} `} />
          <span className={`text-white font-bold ${isIpad ? 'text-[18px]' : 'text-[10px]'} `}>{prices.toFixed(0)}</span>
        </div>
      </div>
    </div>
  )
}

interface PatternAmountProps {
  drawDetails: any
  isMobile?: boolean
}

export const PatternAmount: React.FC<PatternAmountProps> = ({ drawDetails, isMobile }) => {
  const geetprizes = drawDetails && drawDetails.draw.prizes
  const types = Object.keys(geetprizes)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const uniquePatterns: { [key: string]: { type: string; prices: number; winCount: number } } = {}
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { width, height } = useWindowSize()
  types.forEach((type) => {
    if (geetprizes[type] && geetprizes[type].price !== undefined) {
      if (!uniquePatterns[type]) {
        uniquePatterns[type] = { type, prices: geetprizes[type].price, winCount: geetprizes[type].winCount }
      }
    }
  })

  const prizeCounts = Object.values(uniquePatterns).sort((a, b) => b.prices - a.prices)

  useEffect(() => {
    setShowScrollIndicator(prizeCounts.length > 1)
  }, [prizeCounts.length])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    let scrollTimeout: NodeJS.Timeout

    const handleScroll = () => {
      setIsScrolling(true)

      // Check if scrolled to bottom
      if (scrollContainer) {
        const isBottom =
          Math.abs(scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight) < 1
        setIsAtBottom(isBottom)
      }

      clearTimeout(scrollTimeout)

      scrollTimeout = setTimeout(() => {
        setIsScrolling(false)
      }, 200)
    }

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll)

      setIsAtBottom(false)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll)
      }
      clearTimeout(scrollTimeout)
    }
  }, [])

  const isIpad = width === 1010 && height >= 1300
  return (
    <div className={``}>
      <div className="rounded-xl border-2 border-yellow-500 overflow-hidden relative" style={
        isIpad
          ? {
            borderWidth: '4px',
            borderColor: '#eab308',
            borderRadius: '0.75rem',
          }
          : undefined
      }>
        <div
          className={`border-b-2 bg-[#24147F] rounded border-yellow-500  py-[0px] md:py-2 lg:py-1 xl:py-2 md:px-1  lg:px-4 flex items-center justify-center`}
          style={
            isIpad
              ? {
                borderBottomWidth: '4px',
                backgroundColor: '#24147F',
                borderColor: '#eab308',
                borderRadius: '0.25rem',
              }
              : undefined
          }
        >
          <h2 className={`text-center text-[14px]  md:text-base lg:text-md xl:text-xl   text-cyan-400 font-bold`}>
            Pattern Amount
          </h2>

        </div>
        <div
          ref={scrollContainerRef}
          className={`   lg:max-h-[120px]  max-h-[45px] px-1 bg-[#2C1995] cursor-grab overflow-y-auto overflow-x-hidden py-1 md:py-2`} style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {prizeCounts.map((pattern, index) => (
            <PatternItem
              isMobile={isMobile}
              key={index}
              type={pattern.type}
              prices={pattern.prices}
              winCount={pattern.winCount}
              draws={drawDetails.draw.prizes}
              isPatternAmount
            />
          ))}
        </div>

        {showScrollIndicator && !isScrolling && !isAtBottom && (
          <div className="absolute bottom-[-10px] md:bottom-0 left-1/2 transform text-[#FFB818] font-bold animate-bounce">
            <ChevronDown size={36} />
          </div>
        )}
      </div>
    </div>
  );
}
